import type { UserStatus } from '@features/users/api/types/user.types';

import { CobaltActionBar } from '@cae/cobalt-react/components/action-bar';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { useOrganizations } from '@contexts/organizations-provider';
import { CheckboxFilterSection } from '@shared/components/checkbox-filter-section';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { UsersFilterPanelProps } from './UsersFilterPanel.types';

import { useUsersFilter } from '../../contexts/filter/useUsersFilter';
import { useUsersFilterOptions } from '../../hooks/use-users-filter-options/useUsersFilterOptions';
import styles from './UsersFilterPanel.module.css';
import { areArraysEqual } from './UsersFilterPanel.utils';

const UsersFilterPanel = React.memo(
  ({ onClose }: Readonly<UsersFilterPanelProps>): React.ReactElement => {
    const { selectedOrganization } = useOrganizations();
    const { filters, setFilters, clearFilters } = useUsersFilter();

    const {
      data: filterOptions,
      isLoading,
      isError,
    } = useUsersFilterOptions(selectedOrganization?.id ?? '');

    const [panelRole, setPanelRole] = React.useState<string[]>(filters.roles);
    const [panelStatus, setPanelStatus] = React.useState<UserStatus[]>(
      filters.status
    );

    const handleRoleSelect = React.useCallback((id: string): void => {
      setPanelRole(prev =>
        prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
      );
    }, []);

    const handleStatusSelect = React.useCallback((id: string): void => {
      const status = id as UserStatus;
      setPanelStatus(prev =>
        prev.includes(status)
          ? prev.filter(s => s !== status)
          : [...prev, status]
      );
    }, []);

    const handleApply = React.useCallback((): void => {
      setFilters({
        roles: panelRole,
        status: panelStatus,
      });
      onClose();
    }, [panelRole, panelStatus, setFilters, onClose]);

    const handleReset = React.useCallback((): void => {
      clearFilters();
      onClose();
    }, [clearFilters, onClose]);

    const applyDisabled = React.useMemo(() => {
      return (
        areArraysEqual(panelRole, filters.roles) &&
        areArraysEqual(panelStatus, filters.status)
      );
    }, [panelRole, panelStatus, filters.roles, filters.status]);

    const clearDisabled = React.useMemo(() => {
      return (
        panelRole.length === 0 &&
        panelStatus.length === 0 &&
        filters.roles.length === 0 &&
        filters.status.length === 0 &&
        filters.searchText.length === 0
      );
    }, [
      panelRole,
      panelStatus,
      filters.roles,
      filters.status,
      filters.searchText,
    ]);

    const roleOptions = React.useMemo(
      () =>
        (filterOptions?.roles ?? []).map(opt => ({
          id: opt.value,
          name: opt.name,
        })),
      [filterOptions?.roles]
    );

    const statusOptions = React.useMemo(
      () =>
        (filterOptions?.status ?? []).map(opt => ({
          id: opt.value,
          name: opt.name,
        })),
      [filterOptions?.status]
    );

    const hasFilterOptions = React.useMemo(() => {
      return roleOptions.length > 0 || statusOptions.length > 0;
    }, [roleOptions, statusOptions]);

    return (
      <CobaltSidePanel
        isOpen
        showCloseButton
        title="Filters"
        onClose={onClose}
        actionBar={
          hasFilterOptions ? (
            <CobaltActionBar
              position="right"
              variant="floating"
              tertiaryButton={
                <CobaltButton
                  variant="ghost"
                  onClick={handleReset}
                  disabled={clearDisabled}
                >
                  Clear
                </CobaltButton>
              }
              primaryButton={
                <CobaltButton
                  variant="primary"
                  onClick={handleApply}
                  disabled={applyDisabled}
                >
                  Apply
                </CobaltButton>
              }
            />
          ) : undefined
        }
      >
        <div
          className={styles.usersFilterPanel}
          data-testid="users-filter-panel"
        >
          {isLoading && <ContentLoader />}
          {isError && <InlineFeedback type="data-error" />}
          {filterOptions && !hasFilterOptions && (
            <InlineFeedback type="so-empty" />
          )}
          {filterOptions && hasFilterOptions && (
            <>
              <CheckboxFilterSection
                label="Role"
                headingId="users-role-filter-heading"
                dataTestId="users-role-filter"
                options={roleOptions}
                selected={panelRole}
                onSelect={handleRoleSelect}
                hasDivider={false}
              />

              <CheckboxFilterSection
                label="Status"
                headingId="users-status-filter-heading"
                dataTestId="users-status-filter"
                options={statusOptions}
                selected={panelStatus}
                onSelect={handleStatusSelect}
              />
            </>
          )}
        </div>
      </CobaltSidePanel>
    );
  }
);

UsersFilterPanel.displayName = 'UsersFilterPanel';

export { UsersFilterPanel };
